import { EventRepeat } from '@mui/icons-material'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Tab,
  Typography,
} from '@mui/material'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Modal, NavigationLink, UserDetailBlock } from '../../../../components'
import { PatientOrProvider } from '../../../../components/PatientOrProviderDetails/types'
import { useOptions } from '../../../../providers/Options/Options.provider'
import { usePeerGroups } from '../../../../providers/PeerGroups/PeerGroups.provider'
import { useProviderDetails } from '../../../../providers/ProviderDetails/ProviderDetails.provider'
import { useSchedule } from '../../../../providers/Schedule/Schedule.provider'
import {
  Appointment,
  Participant,
  Patient,
} from '../../../../providers/Schedule/types'
import { useVrHeadset } from '../../../../providers/VrHeadset/VrHeadset.provider'
import { themeColors } from '../../../../theme'
import {
  dateFormatters,
  dayUtilities,
  generateZoomWorkspaceLink,
} from '../../../../utilities/HelperFunctions'
import { JoinVRHelpModal } from './JoinVRHelpModal'
import { JoinVRModal } from './JoinVRModal'

interface ScheduleDetailViewProps {
  appointment: Appointment
  returnToListView: () => void
  setShowCancelModalMode: Dispatch<SetStateAction<boolean>>
  setShowRemovePatientModalMode: Dispatch<SetStateAction<boolean>>
  selectPatient: (patient: Patient) => void
  removingPatient: boolean
  addPatientModalOpen: boolean
  setAddPatientModalOpen: Dispatch<SetStateAction<boolean>>
  activeDay: Date | null
}

export const ScheduleDetailView = ({
  appointment,
  returnToListView,
  setShowCancelModalMode,
  setShowRemovePatientModalMode,
  selectPatient,
  setAddPatientModalOpen,
  activeDay,
}: ScheduleDetailViewProps) => {
  const go = useNavigate()
  const { isPaired } = useVrHeadset()
  const { joinVRSession } = usePeerGroups()
  const { options, optionsLoading } = useOptions()
  const { scheduleMode, get7dayAppointments } = useSchedule()
  const { myProviderDetails } = useProviderDetails()
  const { pushPatientIntoPeerGroupSession, pushProviderIntoPeerGroupSession } =
    usePeerGroups()

  const [tabState, setTabState] = useState<string>('patients')
  const [joinModalOpen, setJoinModalOpen] = useState<boolean>(false)
  const [joinHelpModalOpen, setJoinHelpModalOpen] = useState<boolean>(false)
  const [isDisabled, setIsDisabled] = useState<boolean>(false)
  const [openPushOtherPatientModal, setOpenPushOtherPatientModal] =
    useState(false)
  const [openPushOtherProviderModal, setOpenPushOtherProviderModal] =
    useState(false)
  const [pushInProgress, setPushInProgress] = useState(false)
  const [selectedPatientId, setSelectedPatientId] = useState<string>('')
  const [selectedProviderId, setSelectedProviderId] = useState<string>('')

  useEffect(() => {
    const moderatorIds = appointment.participants.map((p) => p.participantId)
    const shouldDisplayPatients = moderatorIds.some(
      (id) => id === myProviderDetails?.providerId
    )

    if (myProviderDetails?.accessRoles && !shouldDisplayPatients) {
      setIsDisabled(
        !(
          myProviderDetails?.accessRoles[0].description === 'SuperAdmin' ||
          myProviderDetails?.accessRoles[0].description === 'Admin'
        )
      )
    }
  }, [scheduleMode, myProviderDetails])

  const handleChange = (_: any, newValue: string) => {
    setTabState(newValue)
  }

  const sessionNotPassed = new Date(appointment.startTime) >= new Date() // If appointment.startTime >= current date, it means the session has not passed

  return (
    <Box sx={{ height: 'inherit' }}>
      {joinModalOpen && (
        <JoinVRModal
          showJoinModal={joinModalOpen}
          setShowJoinModal={setJoinModalOpen}
          handleHelpNavigation={() => {
            setJoinHelpModalOpen(true)
            setJoinModalOpen(false)
          }}
        />
      )}
      {joinHelpModalOpen && (
        <JoinVRHelpModal
          showJoinHelpModal={joinHelpModalOpen}
          setShowJoinHelpModal={setJoinHelpModalOpen}
        />
      )}

      <Grid
        container
        sx={{ height: '100%', overflow: 'auto', position: 'relative' }}
      >
        <Grid item xs={7} sx={{ padding: '5px 0', maxHeight: 'inherit' }}>
          <NavigationLink
            label='Back to schedule'
            direction='backward'
            handleClick={returnToListView}
          />

          <Box sx={{ height: 'max-content' }}>
            <Box sx={{ height: 'max-content' }}>
              <Box sx={{ paddingLeft: 2, paddingRight: 2 }}>
                <Typography
                  sx={{
                    color: themeColors.grayblack,
                    fontSize: '15px',
                    fontWeight: '700',
                  }}
                >
                  {appointment.appointmentName}
                </Typography>

                <Typography
                  sx={{
                    color: themeColors.grayblack,
                    fontSize: '13px',
                    fontWeight: '400',
                  }}
                >
                  {dateFormatters.prettyDate(appointment.startTime)}
                </Typography>

                <Box sx={{ display: 'flex', color: themeColors.gray40 }}>
                  {appointment.isRecurring && (
                    <EventRepeat
                      fontSize='small'
                      sx={{ paddingRight: '5px' }}
                    />
                  )}
                  <Typography
                    sx={{
                      color: themeColors.gray40,
                      fontSize: '13px',
                      fontWeight: '400',
                    }}
                  >
                    {dateFormatters.time(appointment.startTime)} -{' '}
                    {dateFormatters.time(appointment.endTime)}
                  </Typography>
                </Box>

                {appointment.isLastSession && (
                  <Typography
                    color={themeColors.red}
                    sx={{
                      color: themeColors.red,
                      fontSize: '15px',
                      fontWeight: '700',
                    }}
                  >
                    * Last Session *
                  </Typography>
                )}

                {appointment.isVr && (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Chip
                      label={
                        appointment.participantCount -
                        appointment.patients.length
                          ? 'Available'
                          : 'Full'
                      }
                      size='small'
                      sx={{ marginRight: '5px', padding: '1px' }}
                    />
                    <Typography
                      sx={{
                        color: themeColors.gray40,
                        fontSize: '13px',
                        fontWeight: '400',
                      }}
                    >
                      {`${appointment.patients.length}/${appointment.seatsAvailable} Patient Seats Filled`}
                    </Typography>
                  </Box>
                )}
              </Box>

              {appointment.isVr ? (
                <Box>
                  <TabContext value={tabState}>
                    <TabList
                      onChange={handleChange}
                      aria-label='Details Tabs'
                      variant='scrollable'
                      visibleScrollbar={false}
                      TabIndicatorProps={{ style: { display: 'none' } }}
                      sx={{
                        width: '80%',
                        padding: 0,
                        margin: '10px 0 0 20px',
                        '& .MuiTabs-flexContainer': {
                          flexWrap: 'wrap',
                        },
                        '& .Mui-selected': {
                          borderBottom: `5px solid ${themeColors.blue}`,
                        },
                      }}
                    >
                      <Tab
                        label={`Moderators (${
                          appointment.participants.filter(
                            (p) => p.isModerator || p.isCoModerator
                          ).length
                        })`}
                        value={'moderators'}
                        style={{
                          marginRight: '10px',
                          padding: '0',
                        }}
                      />
                      <Tab
                        label={`Patients (${appointment.patients.length})`}
                        value={'patients'}
                        disabled={!appointment.patients?.length || isDisabled}
                        style={{ marginRight: '10px', padding: '0' }}
                      />
                      <Tab
                        label={`Observers (${
                          appointment.participants.filter((p) => p.isObserver)
                            ?.length
                        })`}
                        value={'observers'}
                        disabled={
                          !appointment.participants.filter((p) => p.isObserver)
                            ?.length
                        }
                        style={{ marginRight: '10px', padding: '0' }}
                      />
                      <Tab
                        label={`Other Providers (${
                          appointment.participants.filter(
                            (p) =>
                              !p.isModerator &&
                              !p.isCoModerator &&
                              !p.isObserver
                          )?.length
                        })`}
                        value={'otherProviders'}
                        disabled={
                          !appointment.participants.filter(
                            (p) =>
                              !p.isModerator &&
                              !p.isCoModerator &&
                              !p.isObserver
                          )?.length
                        }
                        style={{ marginRight: '10px', padding: '0' }}
                      />
                    </TabList>

                    <TabPanel
                      value={'patients'}
                      sx={{
                        padding: 0,
                        paddingBottom: 2,
                        margin: 0,
                      }}
                    >
                      <Button
                        variant='contained'
                        sx={{ margin: '25px 20px' }}
                        onClick={() => setOpenPushOtherPatientModal(true)}
                      >
                        Push Other Patent In
                      </Button>

                      <Modal
                        title={'Push Patient into Peer Group Session'}
                        open={openPushOtherPatientModal}
                        setOpen={setOpenPushOtherPatientModal}
                        ModalContent={
                          <Box
                            sx={{
                              p: 2,
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                            }}
                          >
                            <Typography
                              style={{
                                justifySelf: 'center',
                                marginBottom: '10px',
                              }}
                            >
                              Please select a patient you want to push into the
                              session.
                            </Typography>

                            <select
                              value={selectedPatientId}
                              onChange={(e) =>
                                setSelectedPatientId(e.target.value)
                              }
                              defaultValue={''}
                            >
                              <option disabled value={''}>
                                Select a Patient
                              </option>
                              {!optionsLoading &&
                                options?.patients.map((patient) => (
                                  <option
                                    key={patient.patientId}
                                    value={patient.patientId}
                                  >
                                    {patient.preferredName}
                                  </option>
                                ))}
                            </select>

                            <Box
                              sx={{
                                mt: 2,
                                display: 'flex',
                                justifyContent: 'flex-end',
                              }}
                            >
                              <Button
                                variant='outlined'
                                onClick={() =>
                                  setOpenPushOtherPatientModal(false)
                                }
                                sx={{ margin: '0 10px' }}
                                disabled={pushInProgress}
                              >
                                Cancel
                              </Button>

                              <Button
                                variant='contained'
                                disabled={pushInProgress}
                                onClick={async () => {
                                  setPushInProgress(true)

                                  try {
                                    selectedPatientId &&
                                      appointment.appointmentId &&
                                      (await pushPatientIntoPeerGroupSession(
                                        parseInt(selectedPatientId),
                                        appointment.appointmentId
                                      ))

                                    activeDay &&
                                      (await get7dayAppointments(activeDay))

                                    setOpenPushOtherPatientModal(false)
                                  } catch (error) {
                                    console.error('Error:', error)
                                  } finally {
                                    // This ensures setPushInProgress(false) always runs
                                    setPushInProgress(false)
                                  }
                                }}
                              >
                                {pushInProgress ? (
                                  <CircularProgress size={16} color='info' />
                                ) : (
                                  'Confirm'
                                )}
                              </Button>
                            </Box>
                          </Box>
                        }
                      />

                      {!isDisabled &&
                        appointment.patients.map(
                          (patient: Patient, i: number) => (
                            <UserDetailBlock
                              tabType='patients'
                              key={patient.name + i}
                              user={{
                                name: patient.name,
                                photoUrl: patient.photoURL,
                                patientMrn: patient.mrn,
                                patientOrProvider: PatientOrProvider.Patient,
                                isJoined: patient.isJoined,
                                attended: patient.attended,
                                patientUserId: patient.participantId,
                                sessionId: appointment.appointmentId,
                                sessionStartTime: appointment.startTime,
                                sessionEndTime: appointment.endTime,
                                activeDay: activeDay,
                              }}
                              onUserClick={() => {
                                setShowRemovePatientModalMode(true)
                                selectPatient(patient)
                              }}
                              showRemovePatient={sessionNotPassed}
                            />
                          )
                        )}
                    </TabPanel>

                    <TabPanel
                      value={'moderators'}
                      sx={{
                        padding: 0,
                        margin: 0,
                      }}
                    >
                      <Button
                        variant='contained'
                        sx={{ margin: '25px 20px' }}
                        onClick={() => setOpenPushOtherProviderModal(true)}
                      >
                        Push Other Provider In
                      </Button>
                      <Modal
                        title={'Push Provider into Peer Group Session'}
                        open={openPushOtherProviderModal}
                        setOpen={setOpenPushOtherProviderModal}
                        ModalContent={
                          <Box
                            sx={{
                              p: 2,
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                            }}
                          >
                            <Typography
                              style={{
                                justifySelf: 'center',
                                marginBottom: '10px',
                              }}
                            >
                              Please select a provider you want to push into the
                              session.
                            </Typography>

                            <select
                              value={selectedProviderId}
                              onChange={(e) =>
                                setSelectedProviderId(e.target.value)
                              }
                              defaultValue={''}
                            >
                              <option disabled value={''}>
                                Select a Provider
                              </option>
                              {!optionsLoading &&
                                options?.providers
                                  .filter((p) =>
                                    appointment.participants.every(
                                      (ap) => ap.participantId !== p.providerId
                                    )
                                  )
                                  .map((provider) => (
                                    <option
                                      key={provider.providerId}
                                      value={provider.providerId}
                                    >
                                      {provider.name}
                                    </option>
                                  ))}
                            </select>

                            <Box
                              sx={{
                                mt: 2,
                                display: 'flex',
                                justifyContent: 'flex-end',
                              }}
                            >
                              <Button
                                variant='outlined'
                                onClick={() =>
                                  setOpenPushOtherProviderModal(false)
                                }
                                sx={{ margin: '0 10px' }}
                                disabled={pushInProgress}
                              >
                                Cancel
                              </Button>

                              <Button
                                variant='contained'
                                disabled={pushInProgress}
                                onClick={async () => {
                                  setPushInProgress(true)

                                  try {
                                    selectedProviderId &&
                                      appointment.appointmentId &&
                                      (await pushProviderIntoPeerGroupSession(
                                        parseInt(selectedProviderId),
                                        appointment.appointmentId
                                      ))

                                    activeDay &&
                                      (await get7dayAppointments(activeDay))

                                    setOpenPushOtherProviderModal(false)
                                  } catch (error) {
                                    console.error('Error:', error)
                                  } finally {
                                    // This ensures setPushInProgress(false) always runs
                                    setPushInProgress(false)
                                  }
                                }}
                              >
                                {pushInProgress ? (
                                  <CircularProgress size={16} color='info' />
                                ) : (
                                  'Confirm'
                                )}
                              </Button>
                            </Box>
                          </Box>
                        }
                      />
                      {appointment.participants
                        .filter((p) => p.isModerator || p.isCoModerator)
                        .map((participant: Participant, i: number) => (
                          <UserDetailBlock
                            tabType='moderators'
                            key={participant.participantName + i}
                            {...{
                              user: {
                                name: participant.participantName,
                                photoUrl: participant.participantPhotoURL,
                                providerRole: participant.participantRole,
                                patientMrn: participant.participantRole,
                                patientOrProvider: PatientOrProvider.Provider,
                                isModerator: participant.isModerator,
                                isCoModerator: participant.isCoModerator,
                                isObserver: participant.isObserver,
                              },
                            }}
                          />
                        ))}
                    </TabPanel>

                    <TabPanel
                      value={'observers'}
                      sx={{
                        padding: 0,
                        margin: 0,
                      }}
                    >
                      {appointment.participants
                        .filter((p) => p.isObserver)
                        ?.map((participant: Participant, i: number) => (
                          <UserDetailBlock
                            key={participant.participantName + i}
                            {...{
                              user: {
                                name: participant.participantName,
                                photoUrl: participant.participantPhotoURL,
                                providerRole: participant.participantRole,
                                patientMrn: participant.participantRole,
                                patientOrProvider: PatientOrProvider.Provider,
                                isObserver: participant.isObserver,
                              },
                            }}
                          />
                        ))}
                    </TabPanel>
                    <TabPanel
                      value={'otherProviders'}
                      sx={{
                        padding: 0,
                        margin: 0,
                      }}
                    >
                      {appointment.participants
                        .filter(
                          (p) =>
                            !p.isModerator && !p.isCoModerator && !p.isObserver
                        )
                        ?.map((participant: Participant, i: number) => (
                          <UserDetailBlock
                            key={participant.participantName + i}
                            {...{
                              user: {
                                name: participant.participantName,
                                photoUrl: participant.participantPhotoURL,
                                providerRole: participant.participantRole,
                                patientMrn: participant.participantRole,
                                patientOrProvider: PatientOrProvider.Provider,
                                isModerator: participant.isModerator,
                                isCoModerator: participant.isCoModerator,
                                isObserver: participant.isObserver,
                              },
                            }}
                          />
                        ))}
                    </TabPanel>
                  </TabContext>
                </Box>
              ) : (
                appointment.patients.map((participant: Patient, i: number) => (
                  <UserDetailBlock
                    showRemovePatient
                    key={participant.name + i}
                    {...{
                      user: {
                        name: participant.name,
                        photoUrl: participant.photoURL,
                        patientMrn: participant.mrn,
                        patientOrProvider: PatientOrProvider.Patient,
                      },
                    }}
                  />
                ))
              )}
            </Box>
          </Box>
        </Grid>
        <Grid
          item
          xs={5}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            position: 'sticky',
            top: 0,
            padding: '10px',
            alignSelf: 'flex-start',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              margin: 1,
              marginBottom: 1.5,
            }}
          >
            {appointment.isVr ? (
              <>
                <Button
                  disableRipple
                  variant='contained'
                  color='info'
                  sx={{ margin: '0px 10px' }}
                  onClick={() => setAddPatientModalOpen(true)}
                >
                  Add Patient to Peer Group
                </Button>

                {!isPaired ? (
                  <Button
                    disableRipple
                    variant='outlined'
                    color='error'
                    onClick={() => go('/account')}
                  >
                    Pair headset to join VR
                  </Button>
                ) : (
                  <Button
                    disableRipple
                    variant='contained'
                    onClick={async () => {
                      await joinVRSession(appointment.appointmentId!)
                      setJoinModalOpen(true)
                    }}
                  >
                    Join VR
                  </Button>
                )}
              </>
            ) : appointment.meetingURL ? (
              <Button
                disableRipple
                variant='contained'
                href={generateZoomWorkspaceLink(appointment.meetingURL)}
                target='_blank'
              >
                Open in Zoom App
              </Button>
            ) : (
              <Typography color='error' sx={{ fontSize: '14px' }}>
                No Zoom Link
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              width: '100%',
              minHeight: 180,
              borderRadius: 0.8,
              padding: 1.5,
              border: `2px solid ${themeColors.gray70}`,
            }}
          >
            <Typography
              sx={{
                color: themeColors.gray40,
                fontSize: '12px',
                fontWeight: '400',
              }}
            >
              {appointment.appointmentDescription}
            </Typography>
          </Box>

          {!appointment.isVr ? (
            <Typography
              sx={{
                color: themeColors.gray40,
                fontSize: '13px',
                fontWeight: '700',
                marginTop: '5px',
              }}
            >
              *Patient must consent before rescheduling.
            </Typography>
          ) : null}

          {appointment.isVr ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              {myProviderDetails?.accessRoles &&
                (myProviderDetails?.accessRoles[0].description === 'Admin' ||
                  myProviderDetails?.accessRoles[0].description ===
                    'SuperAdmin') && (
                  <Box sx={{ margin: 1, marginTop: 1.5 }}>
                    <Button
                      variant='outlined'
                      color='error'
                      disabled={isDisabled}
                      onClick={() => setShowCancelModalMode(true)}
                    >
                      Cancel Session
                    </Button>
                  </Box>
                )}
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <Box sx={{ margin: 1, marginTop: 1 }}>
                <Button
                  variant='contained'
                  color='error'
                  href={appointment.cancelURL}
                  target='_blank'
                  onClick={returnToListView}
                >
                  Cancel
                </Button>
              </Box>
              <Box sx={{ margin: 1, marginTop: 1 }}>
                <Button
                  variant='outlined'
                  href={appointment.rescheduleURL}
                  target='_blank'
                >
                  Reschedule*
                </Button>
              </Box>
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  )
}
